import React, { useEffect, useState } from 'react';
import errorReporter from '@app/errorReporter';
import { SupraLock, getListingSupraLocks } from 'api/SupraLockController';
import Swal from 'sweetalert2';
import styled from 'styled-components';
import { ConfirmationInstructionsJsonType } from '@bb/domain/dist/types/AppointmentConfig';
import { ListingLabelContext } from 'contexts';
import { SupraSystemSubscriptionStatus } from '@bb/domain/dist/types/SupraSystemSubscription';

import { Select, SelectOption } from '../Select';
import { Switch } from '../Switch';
import { Text } from '../Typography';

interface SupraLockValidatorProps {
  setGetLockboxesFalse: () => void;
  shouldGetLockboxes: boolean;
  listingId: string;
  listingReps: any[];
  setIsSupraLockCodeValid: (isValid: boolean) => void;
  supraLockInfo: Partial<ConfirmationInstructionsJsonType['supralockbox']> & {
    canModifyAccessByShowing?: boolean;
  };
  setSupraLockInfo: (
    info: Partial<ConfirmationInstructionsJsonType['supralockbox']>,
    reassign?: boolean,
  ) => void;
  supraSubscriptionStatus?: SupraSystemSubscriptionStatus;
  isAdmin?: boolean;
}

const SupraLockValidator = ({
  setGetLockboxesFalse,
  shouldGetLockboxes,
  listingId,
  setIsSupraLockCodeValid,
  supraLockInfo,
  setSupraLockInfo,
  supraSubscriptionStatus,
  isAdmin,
}: SupraLockValidatorProps) => {
  const [selected, setSelected] = useState<SupraLock | null>(null);
  const [locks, setLocks] = useState<SupraLock[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { getAvailability } = React.useContext(ListingLabelContext);

  useEffect(() => {
    if (selected?.serialNumber) {
      setIsSupraLockCodeValid(true);
    } else {
      setIsSupraLockCodeValid(false);
    }
  }, [selected]);

  const deselect = () => {
    setSupraLockInfo({
      serialNumber: '',
      productCode: undefined,
    });
    setIsSupraLockCodeValid(false);
    setSelected(null);
  };

  const select = (selectedLock: SupraLock) => {
    if (selectedLock.listing && selectedLock.listing._id !== listingId) {
      Swal.fire({
        title: 'Lockbox Is Already Assigned',
        html: ` <div style="font-weight: 200;"><div>This lockbox is already assigned to another listing: </div>
              <br/>
              <div>${selectedLock.listing.line1} ${
                selectedLock.listing.line2 || ''
              }</div>
              <div>Listing Status: ${getAvailability(
                selectedLock.listing.availability,
              )}</div></div>
              <br/>
              <div>Would you like to remove it and re-assign it to this listing?</div>
              `,
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#62cb31',
        confirmButtonText: 'Yes, re-assign',
        cancelButtonText: 'Cancel',
      }).then(result => {
        if (result.dismiss) {
          deselect();
        } else {
          setSupraLockInfo(
            {
              serialNumber: selectedLock.serialNumber,
              productCode: selectedLock.productCode,
              accessByShowingOnly: supraLockInfo?.accessByShowingOnly,
              version: selectedLock.version,
            },
            !selectedLock.version.startsWith('06') &&
              !selectedLock.version.startsWith('08'),
          );
        }
      });
    } else {
      setSupraLockInfo(
        {
          serialNumber: selectedLock.serialNumber,
          productCode: selectedLock.productCode,
          accessByShowingOnly: supraLockInfo?.accessByShowingOnly,
          version: selectedLock.version,
        },
        !selectedLock.version.startsWith('06') &&
          !selectedLock.version.startsWith('08'),
      );
    }
  };

  const getAllSupraLocks = async () => {
    const listingLocks: SupraLock[] = await getListingSupraLocks(listingId);

    listingLocks.sort((a, b) => (a.serialNumber > b.serialNumber ? 1 : -1));
    return listingLocks;
  };

  const fetchListingSupraLocks = async () => {
    try {
      setIsLoading(true);
      const fetchedLocks = await getAllSupraLocks();
      setLocks(fetchedLocks);
      const currentLockbox = fetchedLocks.find(
        ({ serialNumber }) => serialNumber === supraLockInfo?.serialNumber,
      );

      if (currentLockbox) {
        setSelected(currentLockbox);
      } else {
        deselect();
      }
    } catch (error) {
      errorReporter.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (listingId) {
      fetchListingSupraLocks();
    }
  }, [listingId]);

  useEffect(() => {
    if (shouldGetLockboxes) {
      fetchListingSupraLocks();
      setGetLockboxesFalse();
    }
  }, [shouldGetLockboxes]);

  const handleAccessByShowing = (checked: boolean) => {
    setSupraLockInfo({
      accessByShowingOnly: checked,
    });
  };

  return (
    <div>
      <StyledLockboxValidatorContainer>
        {locks.length > 0 && (
          <Select
            showSearch
            placeholder="Select"
            value={selected?.serialNumber}
            allowClear
            listHeight={230}
            disabled={
              !isAdmin &&
              supraSubscriptionStatus !==
                SupraSystemSubscriptionStatus.subscribed
            }
            onChange={value => {
              const selectedLock = locks.find(
                ({ serialNumber }) => serialNumber === value,
              );
              if (selectedLock) {
                setSelected(selectedLock);
                select(selectedLock);
              } else {
                deselect();
              }
            }}
          >
            {locks?.map(lock => (
              <SelectOption key={lock.serialNumber} value={lock.serialNumber}>
                {lock.productCode === 'iBox' ? 'iBox BT LE' : 'Invoy'}{' '}
                {lock.serialNumber}
              </SelectOption>
            ))}
          </Select>
        )}

        {selected &&
          !selected.version.startsWith('06') &&
          !selected.version.startsWith('08') && (
            <>
              <StyledSwitchContainer>
                <div>
                  <StyledSwitchText>
                    Access by Appointment only
                  </StyledSwitchText>
                  <p>
                    {supraLockInfo?.accessByShowingOnly
                      ? 'This lockbox can only be opened during a valid showing appointment.'
                      : 'This lockbox has no appointment restrictions.'}
                  </p>
                </div>
                <Switch
                  onChange={handleAccessByShowing}
                  checked={supraLockInfo?.accessByShowingOnly}
                  disabled={
                    (!isAdmin &&
                      supraSubscriptionStatus !==
                        SupraSystemSubscriptionStatus.subscribed) ||
                    supraLockInfo?.canModifyAccessByShowing === false
                  }
                />
              </StyledSwitchContainer>
            </>
          )}
        {!locks?.length && !isLoading && (
          <label className="error">No available lockboxes.</label>
        )}
      </StyledLockboxValidatorContainer>
    </div>
  );
};

export default SupraLockValidator;

const StyledLockboxValidatorContainer = styled.div`
  position: relative;
  > input.form-control {
    display: inline-block;
    width: 75%;
    margin: 0;
    border-radius: 4px 0 0 4px;
    &.valid {
      border-color: #62cb31 !important;
    }
    &.invalid {
      border-color: #e74c3c !important;
    }
  }
  > button {
    position: absolute;
    display: inline-block;
    width: 25%;
    height: 100%;
    margin: 0;
    right: 0;
    border-left: 0;
    border-radius: 0 4px 4px 0;
  }
`;

const StyledSwitchContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 10px;
  margin-top: 10px;
`;

const StyledSwitchText = styled(Text)`
  font-weight: bold;
`;
